<script setup lang="ts">
// Hiding parts of a screenshot before it leaves this browser (2026-10-27): the reporter's in the Feedback dialog, and an
// organisation admin's copy for an excerpt. Drag on the picture, or "Hide an area" and the arrow keys (shift: its size);
// Delete removes it. The areas are in the picture's own pixels; `exportPng` paints them in.
import { nextTick, onMounted, ref, watch } from "vue";
import Icon from "@/components/ui/Icon.vue";
import type { Area } from "./capture";

const props = defineProps<{ base: HTMLCanvasElement; label: string }>();
const areas = defineModel<Area[]>({ required: true });
const preview = ref<HTMLCanvasElement | null>(null);
const stage = ref<HTMLDivElement | null>(null);
const selected = ref(-1);

function draw() {
  const c = preview.value, base = props.base;
  if (!c) return;
  const w = Math.min(base.width, (stage.value?.clientWidth || 600));
  c.width = w;
  c.height = Math.round(base.height * (w / base.width));
  c.getContext("2d")?.drawImage(base, 0, 0, c.width, c.height);
}
onMounted(() => nextTick(draw));
watch(() => props.base, () => { areas.value = []; selected.value = -1; void nextTick(draw); });   // a new picture: its own hidden areas
defineExpose({ draw });

const pct = (a: Area) => {
  const b = props.base;
  return { left: `${(a.x / b.width) * 100}%`, top: `${(a.y / b.height) * 100}%`, width: `${(a.w / b.width) * 100}%`, height: `${(a.h / b.height) * 100}%` };
};
let drag: { x: number; y: number } | null = null;
function toBase(e: PointerEvent) {
  const r = stage.value!.getBoundingClientRect(), b = props.base;
  return { x: ((e.clientX - r.left) / r.width) * b.width, y: ((e.clientY - r.top) / r.height) * b.height };
}
function down(e: PointerEvent) {
  if (e.button !== 0) return;
  drag = toBase(e);
  areas.value = [...areas.value, { x: drag.x, y: drag.y, w: 0, h: 0 }];
  selected.value = areas.value.length - 1;
  (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
}
function move(e: PointerEvent) {
  if (!drag) return;
  const p = toBase(e), a = areas.value[selected.value];
  Object.assign(a, { x: Math.min(drag.x, p.x), y: Math.min(drag.y, p.y), w: Math.abs(p.x - drag.x), h: Math.abs(p.y - drag.y) });
  areas.value = [...areas.value];
}
function up() {
  if (!drag) return;
  drag = null;
  const a = areas.value[selected.value];
  if (a && (a.w < 6 || a.h < 6)) { areas.value = areas.value.filter((_, i) => i !== selected.value); selected.value = -1; }
}
function addArea() {
  const b = props.base;
  areas.value = [...areas.value, { x: b.width / 2 - 120, y: b.height / 2 - 60, w: 240, h: 120 }];
  selected.value = areas.value.length - 1;
  nextTick(() => stage.value?.querySelector<HTMLElement>(`[data-area="${selected.value}"]`)?.focus());
}
function removeArea(i: number) { areas.value = areas.value.filter((_, j) => j !== i); selected.value = -1; }
function undo() { areas.value = areas.value.slice(0, -1); selected.value = -1; }
function key(e: KeyboardEvent, i: number) {
  const a = { ...areas.value[i] }, step = 10, b = props.base;
  if (e.key === "Delete" || e.key === "Backspace") { removeArea(i); e.preventDefault(); return; }
  const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
  if (!d) return;
  e.preventDefault();
  if (e.shiftKey) { a.w = Math.max(10, a.w + d[0]); a.h = Math.max(10, a.h + d[1]); }
  else { a.x = Math.min(Math.max(0, a.x + d[0]), b.width - a.w); a.y = Math.min(Math.max(0, a.y + d[1]), b.height - a.h); }
  areas.value = areas.value.map((x, j) => (j === i ? a : x));
}
</script>

<template>
  <div ref="stage" class="stage" @pointerdown="down" @pointermove="move" @pointerup="up" @pointercancel="up">
    <canvas ref="preview" class="preview" :aria-label="label" role="img" />
    <button v-for="(a, i) in areas" :key="i" type="button" class="area" :class="{ on: selected === i }" :style="pct(a)"
            :data-area="i" :aria-label="`Hidden area ${i + 1}: arrow keys move it, shift and arrows resize it, Delete removes it`"
            @pointerdown.stop @focus="selected = i" @keydown="key($event, i)" />
  </div>
  <div class="tools">
    <button type="button" class="btn btn--sm" @click="addArea"><Icon name="eye" :size="14" />Hide an area</button>
    <button type="button" class="btn btn--sm btn--ghost" :disabled="!areas.length" @click="undo">Undo</button>
    <slot />
  </div>
  <ul v-if="areas.length" class="hidden-list small">
    <li v-for="(_, i) in areas" :key="i">Hidden area {{ i + 1 }} <button type="button" class="linkish" @click="removeArea(i)">Show it again</button></li>
  </ul>
</template>

<style scoped>
.stage { position: relative; border: 1px solid var(--line); border-radius: var(--radius-s); overflow: hidden; cursor: crosshair; touch-action: none; }
.preview { display: block; width: 100%; height: auto; }
.area { position: absolute; padding: 0; border: 2px solid transparent; background: repeating-linear-gradient(45deg, #6b7280 0 8px, #5b6270 8px 16px); cursor: pointer; }
.area.on, .area:focus-visible { border-color: var(--brand); outline: none; box-shadow: 0 0 0 2px var(--bg-elev); }
.tools { display: flex; flex-wrap: wrap; gap: var(--space-2); }
.hidden-list { margin: 0; padding-left: var(--space-5); color: var(--muted); font-size: var(--text-xs); }
.linkish { background: none; border: 0; padding: 0; color: var(--brand); cursor: pointer; font-size: inherit; }
</style>
